import { Link, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import FourCTooltip from '../components/common/FourCTooltip'
import { 
  CheckCircleIcon, 
  AcademicCapIcon,
  ClockIcon,
  UserGroupIcon,
  TrophyIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/solid'
import { 
  CodeBracketIcon,
  DocumentTextIcon,
  BeakerIcon,
  RocketLaunchIcon 
} from '@heroicons/react/24/outline'
import Footer from '../components/Footer'

const stats = [
  { label: 'Course Modules', value: '10', icon: DocumentTextIcon },
  { label: 'Hands-On Labs', value: '4', icon: BeakerIcon },
  { label: 'Capstone Projects', value: '4', icon: CodeBracketIcon },
  { label: 'Real Case Studies', value: '4+', icon: RocketLaunchIcon },
]

const trustIndicators = [
  { icon: ShieldCheckIcon, text: 'Industry-Proven Curriculum' },
  { icon: AcademicCapIcon, text: 'Professional Certificate' },
  { icon: ClockIcon, text: 'Lifetime Access + Updates' },
  { icon: UserGroupIcon, text: 'Expert Instructor Support' },
]

const capstones = [
  {
    title: 'Support Triage Agent',
    description: 'Classify, draft replies, cite your KB, escalate safely.',
    number: 1,
  },
  {
    title: 'Data Analyst Agent',
    description: 'CSV → EDA summary, charts, and actions.',
    number: 2,
  },
  {
    title: 'Research Copilot',
    description: 'Answers with 3–5 credible citations.',
    number: 3,
  },
  {
    title: 'Workflow Orchestrator',
    description: 'Multi-step automation with YAML workflows.',
    number: 4,
  },
]

const features = [
  'Objective-first prompting (4C pattern)',
  'Structured outputs & JSON contracts',
  'Agent loops & tool use (Assistants)',
  'File Search / RAG & evaluation',
  'Safety, HITL, and cost control',
  'Production deployment strategies',
]

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'Senior AI Engineer, TechCorp',
    image: '👩‍💻',
    content: 'This course transformed how I build AI features. The capstone projects alone were worth the investment—I shipped a production RAG system within 2 weeks.',
    rating: 5,
  },
  {
    name: 'Marcus Rodriguez',
    role: 'Founder, AI Startup',
    image: '👨‍💼',
    content: 'Finally, a course that goes beyond theory. The evaluation harness and safety modules saved me weeks of trial and error. Highly recommended for anyone building real products.',
    rating: 5,
  },
  {
    name: 'Jennifer Park',
    role: 'Product Manager, Enterprise SaaS',
    image: '👩‍🔬',
    content: 'As a PM without a deep AI background, this course gave me the knowledge to spec features confidently and communicate with my engineering team effectively.',
    rating: 5,
  },
]

const faqs = [
  {
    question: 'What prerequisites do I need?',
    answer: 'You\'ll need: (1) Basic Python knowledge, (2) An OpenAI API key (sign up at platform.openai.com - ~$5-20 for the course), and (3) A free GitHub account for code submissions. No prior AI/ML experience needed—we cover LLM fundamentals from the ground up.',
  },
  {
    question: 'How long does the course take?',
    answer: 'Most students complete the course in 4-6 weeks with 5-10 hours per week. You have lifetime access, so you can learn at your own pace.',
  },
  {
    question: 'What if I get stuck?',
    answer: 'You have access to our expert support forum, detailed documentation, and comprehensive code examples. Common issues are addressed in our debugging module.',
  },
  {
    question: 'Is this course up-to-date?',
    answer: 'Yes! This is the OpenAI Assistants Edition, covering the latest APIs. You receive all future updates for free as the platform evolves.',
  },
  {
    question: 'Do I get a certificate?',
    answer: 'Yes! Upon completing all modules and capstone projects, you\'ll receive a professional certificate you can share on LinkedIn and your portfolio.',
  },
  {
    question: 'What\'s your refund policy?',
    answer: '30-day money-back guarantee, no questions asked. If you\'re not satisfied within the first 30 days, we\'ll issue a full refund.',
  },
]

export default function Landing() {
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const [showPricingPrompt, setShowPricingPrompt] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Check if user came from login/register to enroll
  useEffect(() => {
    if (searchParams.get('enroll') === 'true') {
      setShowPricingPrompt(true)
      // Scroll to pricing section
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [searchParams])

  useEffect(() => {
    if (showPricingPrompt) {
      const timer = setTimeout(() => {
        setShowPricingPrompt(false)
      }, 30000) // Hide after 30 seconds
      
      return () => clearTimeout(timer)
    }
  }, [showPricingPrompt])

  const handleEnrollClick = () => {
    setShowPricingPrompt(true)
    // Scroll will happen via href="#pricing"
  }

  return (
    <div className="bg-white">
      {/* Header/Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Branding */}
            <div className="flex items-center space-x-2">
              <img 
                src="/branding/cte-course-platform-icon.svg" 
                alt="CTE Course Platform" 
                className="h-8 w-8"
              />
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cte-blue to-cte-blue-dark bg-clip-text text-transparent">
                CTE Course Platform
              </span>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="#syllabus" className="text-gray-600 hover:text-cte-blue transition-colors font-medium">
                Curriculum
              </a>
              <a href="#capstones" className="text-gray-600 hover:text-cte-blue transition-colors font-medium">
                Projects
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-cte-blue transition-colors font-medium">
                Pricing
              </a>
              {user ? (
                <Link
                  to="/dashboard"
                  className="bg-cte-blue text-white px-5 py-2.5 rounded-lg hover:bg-cte-blue-dark transition-colors font-semibold shadow-sm hover:shadow-md"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-cte-blue transition-colors font-medium"
                  >
                    Sign In
                  </Link>
                  <a
                    href="#pricing"
                    onClick={handleEnrollClick}
                    className="bg-cte-blue text-white px-5 py-2.5 rounded-lg hover:bg-cte-blue-dark transition-colors font-semibold shadow-sm hover:shadow-md"
                  >
                    Enroll Now
                  </a>
                </>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileNavOpen(prev => !prev)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-cte-blue hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cte-blue"
              aria-expanded={mobileNavOpen}
              aria-label="Toggle navigation menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileNavOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Nav Panel */}
          {mobileNavOpen && (
            <div className="md:hidden mt-4 space-y-4 animate-fade-in" role="menu">
              <a
                href="#syllabus"
                onClick={() => setMobileNavOpen(false)}
                className="block text-gray-700 font-medium px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                Curriculum
              </a>
              <a
                href="#capstones"
                onClick={() => setMobileNavOpen(false)}
                className="block text-gray-700 font-medium px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                Projects
              </a>
              <a
                href="#pricing"
                onClick={() => { handleEnrollClick(); setMobileNavOpen(false); }}
                className="block text-gray-700 font-medium px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                Pricing
              </a>
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileNavOpen(false)}
                  className="block bg-cte-blue text-white text-center px-4 py-2.5 rounded-lg font-semibold shadow-sm hover:bg-cte-blue-dark"
                >
                  Dashboard
                </Link>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    onClick={() => setMobileNavOpen(false)}
                    className="block text-gray-700 font-medium px-3 py-2 rounded-lg hover:bg-gray-50 text-center"
                  >
                    Sign In
                  </Link>
                  <a
                    href="#pricing"
                    onClick={() => { handleEnrollClick(); setMobileNavOpen(false); }}
                    className="block bg-cte-blue text-white text-center px-4 py-2.5 rounded-lg font-semibold shadow-sm hover:bg-cte-blue-dark"
                  >
                    Enroll Now
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative overflow-hidden bg-gradient-to-br from-cte-blue via-cte-blue-dark to-indigo-900">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/20">
              <SparklesIcon className="h-4 w-4 mr-2" />
              OpenAI Assistants Edition
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight">
              Master AI Prompting &<br />
              <span className="bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
                Agent Systems
              </span>
            </h1>
            
            <p className="mt-6 text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Build production-ready AI agents with source-backed reliability. 
              Go from prompt fundamentals to shipping monetizable features.
            </p>

            {/* Trust Indicators */}
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/90">
              {trustIndicators.map((indicator, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <indicator.icon className="h-5 w-5 text-blue-200" />
                  <span className="text-sm font-medium">{indicator.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                onClick={handleEnrollClick}
                className="inline-flex items-center justify-center bg-white text-cte-blue font-bold px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
              >
                Start Learning Today
                <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <a
                href="#syllabus"
                className="inline-flex items-center justify-center border-2 border-white text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all backdrop-blur-sm"
              >
                Explore Curriculum
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <stat.icon className="h-8 w-8 text-blue-200 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-blue-100 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-12 md:h-20 text-white" preserveAspectRatio="none" viewBox="0 0 1200 120" fill="currentColor">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-20">
        
        {/* Company Branding Section */}
        <section className="mb-20 text-center">
          <div className="inline-flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-full border border-gray-200">
            <ShieldCheckIcon className="h-6 w-6 text-cte-blue" />
            <span className="text-sm font-semibold text-gray-700">
              Powered by{' '}
              <a 
                href="https://www.cteconsultants.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cte-blue hover:text-cte-blue-dark font-bold"
              >
                CTE Consultants & General Sales LLC
              </a>
            </span>
          </div>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Trusted by professionals seeking to master AI agent development and prompt engineering
          </p>
        </section>

        {/* Capstone Projects */}
        <section id="capstones" className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-4">
              Build 4 Production-Ready Capstone Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Apply your learning by building real-world AI agents you can add to your portfolio
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capstones.map((capstone) => (
              <div
                key={capstone.number}
                className="group border-2 border-gray-200 rounded-2xl p-6 hover:border-cte-blue hover:shadow-xl transition-all transform hover:-translate-y-1 bg-white"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-bold text-cte-blue bg-blue-50 px-3 py-1 rounded-full">
                    Capstone {capstone.number}
                  </span>
                  <div className="w-10 h-10 bg-gradient-to-br from-cte-blue to-cte-blue-dark rounded-lg flex items-center justify-center text-white font-bold group-hover:scale-110 transition-transform">
                    {capstone.number}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-cte-black mb-3 group-hover:text-cte-blue transition-colors">
                  {capstone.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{capstone.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* What You'll Learn */}
        <section className="mb-20 bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-10 md:p-14 border border-gray-200">
          <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-8 text-center">
            Master the Complete AI Agent Stack
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-cte-blue to-cte-blue-dark rounded-lg flex items-center justify-center">
                    <CheckCircleIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <span className="ml-4 text-gray-700 font-medium leading-relaxed">
                  {feature}
                  {feature.includes('4C') && <FourCTooltip inline />}
                </span>
              </div>
            ))}
          </div>
          
          {/* Additional value props */}
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6">
              <CodeBracketIcon className="h-10 w-10 text-cte-blue mx-auto mb-3" />
              <h3 className="font-bold text-cte-black mb-2">Hands-On Coding</h3>
              <p className="text-sm text-gray-600">Real Python implementations, not just theory</p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <DocumentTextIcon className="h-10 w-10 text-cte-blue mx-auto mb-3" />
              <h3 className="font-bold text-cte-black mb-2">Complete Resources</h3>
              <p className="text-sm text-gray-600">Code samples, templates, and checklists</p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <RocketLaunchIcon className="h-10 w-10 text-cte-blue mx-auto mb-3" />
              <h3 className="font-bold text-cte-black mb-2">Production-Ready</h3>
              <p className="text-sm text-gray-600">Ship features to real users with confidence</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20 relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-br from-cte-blue via-cte-blue-dark to-indigo-900"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          </div>
          
          <div className="relative px-8 py-16 md:p-16 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/20">
              <TrophyIcon className="h-4 w-4 mr-2" />
              Limited Time Offer
            </div>
            
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to Build Production AI Agents?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8 leading-relaxed">
              Join hundreds of developers mastering AI agent systems. Get lifetime access, 
              all updates, and a professional certificate upon completion.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="#pricing"
                onClick={handleEnrollClick}
                className="inline-flex items-center justify-center bg-white text-cte-blue font-bold px-10 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
              >
                Enroll Now
                <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <a
                href="#syllabus"
                className="inline-flex items-center justify-center border-2 border-white text-white font-bold px-10 py-4 rounded-xl hover:bg-white/10 transition-all backdrop-blur-sm"
              >
                View Full Curriculum
              </a>
            </div>

            {/* Trust elements */}
            <div className="flex flex-wrap justify-center gap-6 text-white/90 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="h-5 w-5" />
                <span>Lifetime Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="h-5 w-5" />
                <span>Certificate Included</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="h-5 w-5" />
                <span>Free Updates</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="h-5 w-5" />
                <span>Expert Support</span>
              </div>
            </div>
          </div>
        </section>

        {/* Course Modules Preview */}
        <section id="syllabus" className="mt-16">
          <h2 className="text-3xl font-extrabold text-cte-black mb-6">Course Outline</h2>
          <p className="text-gray-600 mb-8">
            10 comprehensive modules covering everything from prompt engineering fundamentals to production deployment.
          </p>
          <div className="space-y-4">
            {[
              {
                number: 1,
                title: 'LLM Foundations for Better Prompting',
                description: 'Understand how LLMs work, token limits, temperature settings, and core concepts that inform effective prompting strategies.',
                difficulty: 'Beginner',
              },
              {
                number: 2,
                title: 'Objective-First Prompting (4C Pattern)',
                description: 'Master the 4C framework (Context, Constraint, Clarity, Criteria) to write prompts that consistently deliver quality results.',
                difficulty: 'Beginner',
              },
              {
                number: 3,
                title: 'Self-Critique & Fact-Bounded Prompts',
                description: 'Build prompts that self-verify, cite sources, and stay grounded in factual information to reduce hallucinations.',
                difficulty: 'Intermediate',
              },
              {
                number: 4,
                title: 'Prompt Debugging & Hallucination Control',
                description: 'Learn systematic debugging techniques and strategies to identify and fix common prompt failures and hallucination issues.',
                difficulty: 'Intermediate',
              },
              {
                number: 5,
                title: 'JSON-First Outputs & Schemas',
                description: 'Generate structured, predictable outputs using JSON schemas, function calling, and strict output formats for reliable integrations.',
                difficulty: 'Intermediate',
              },
              {
                number: 6,
                title: 'Agent Basics: Assistants Concepts',
                description: 'Introduction to OpenAI Assistants API - threads, runs, messages, and building your first conversational agent.',
                difficulty: 'Intermediate',
              },
              {
                number: 7,
                title: 'RAG with Assistants File Search & Vector Stores',
                description: 'Implement Retrieval-Augmented Generation using Assistants API file search, vector stores, and knowledge base integration.',
                difficulty: 'Advanced',
              },
              {
                number: 8,
                title: 'Evaluation, Safety & Cost Control',
                description: 'Set up evaluation harnesses, implement safety guardrails, monitor costs, and ensure production-ready quality.',
                difficulty: 'Advanced',
              },
              {
                number: 9,
                title: 'Planner→Executor Patterns with Tools',
                description: 'Build multi-step agents using planner-executor patterns, function calling, and tool orchestration for complex workflows.',
                difficulty: 'Advanced',
              },
              {
                number: 10,
                title: 'Shipping & Monetizing Agent Features',
                description: 'Deploy to production, handle rate limits, implement usage tracking, and strategies for monetizing your AI features.',
                difficulty: 'Advanced',
              },
            ].map((module) => (
              <a
                key={module.number}
                href="#pricing"
                onClick={handleEnrollClick}
                className="block p-6 border border-gray-200 rounded-xl hover:border-cte-blue hover:shadow-md transition-all group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-cte-blue text-white rounded-full flex items-center justify-center text-sm font-bold group-hover:scale-110 transition-transform">
                    {module.number}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-cte-black group-hover:text-cte-blue transition-colors inline-flex items-center">
                        {module.title}
                        {module.title.includes('4C') && <FourCTooltip inline />}
                      </h3>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        module.difficulty === 'Beginner' 
                          ? 'bg-green-100 text-green-800' 
                          : module.difficulty === 'Intermediate'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {module.difficulty}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                      {module.description}
                    </p>
                    <div className="mt-3 text-xs text-cte-blue font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Enroll to access this module →
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="mb-20 mt-32">
          {/* Pricing Prompt Banner */}
          {showPricingPrompt && (
            <div className="mb-8 animate-fade-in">
              <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cte-blue rounded-full flex items-center justify-center">
                      <SparklesIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      👋 Choose Your Perfect Package
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Select a tier below to get started. All packages include lifetime access, 
                      course updates, and our money-back guarantee. Click &quot;Purchase Now&quot; on your
                      chosen tier to continue.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPricingPrompt(false)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Dismiss message"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-4">
              Choose Your Learning Path
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              One payment. Lifetime access. No recurring fees.
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            {/* Pricing Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mt-8">
              
              {/* Tier 1: Core Access */}
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Core Access
                  </h3>
                  <p className="text-gray-600 text-xs">
                    Just the essentials
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">$147</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">one-time payment</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">10 Course Modules</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">4 Hands-On Labs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">4 Capstone Projects</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Source Code Access</span>
                  </li>
                </ul>

                <Link
                  to="/checkout?tier=core"
                  className="block w-full text-center bg-gray-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Get Started
                </Link>
              </div>

              {/* Tier 2: Complete Package */}
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Complete Package
                  </h3>
                  <p className="text-gray-600 text-xs">
                    Course + certificate
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">$197</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">one-time payment</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-gray-900">Everything in Core, plus:</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Professional Certificate</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">All Future Updates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">LinkedIn Badge</span>
                  </li>
                </ul>

                <Link
                  to="/checkout?tier=complete"
                  className="block w-full text-center bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Choose Complete
                </Link>
              </div>

              {/* Tier 3: Pro Bundle */}
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Pro Bundle
                  </h3>
                  <p className="text-gray-600 text-xs">
                    Complete + support
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-gray-900">$297</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">one-time payment</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-gray-900">Everything in Complete, plus:</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Priority Email Support</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Code Review on Labs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Bonus Resources Pack</span>
                  </li>
                </ul>

                <Link
                  to="/checkout?tier=pro"
                  className="block w-full text-center bg-purple-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Choose Pro
                </Link>
              </div>

              {/* Tier 4: Premium Complete (Most Popular) */}
              <div className="relative bg-gradient-to-br from-white to-blue-50 rounded-xl border-2 border-cte-blue shadow-2xl p-6 hover:shadow-3xl transition-all">
                {/* Popular Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                    🎓 BEST VALUE
                  </span>
                </div>

                <div className="text-center mb-4 pt-2">
                  <h3 className="text-lg font-bold text-cte-blue mb-1">
                    Premium Complete
                  </h3>
                  <p className="text-gray-600 text-xs">
                    Everything included
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-gray-400 line-through text-sm font-semibold">$997</span>
                    <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
                      Save $500
                    </span>
                  </div>
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-extrabold text-cte-blue">$497</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">one-time payment</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-gray-900">Everything in Pro, plus:</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Premium Templates Library</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Exclusive Case Studies</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">All Future Course Content</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">VIP Discord Access</span>
                  </li>
                </ul>

                <Link
                  to="/checkout?tier=premium"
                  className="block w-full text-center bg-gradient-to-r from-cte-blue to-cte-blue-dark text-white text-sm font-bold px-4 py-3 rounded-lg hover:shadow-2xl transition-all transform hover:-translate-y-0.5"
                >
                  <TrophyIcon className="h-4 w-4 inline mr-1" />
                  Get Premium
                </Link>
                <p className="text-xs text-gray-600 text-center mt-2">
                  🔒 30-day guarantee
                </p>
              </div>

              {/* Tier 5: Elite Mentorship */}
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border-2 border-yellow-500 shadow-2xl p-6 hover:shadow-3xl transition-all opacity-90">
                {/* Coming Soon Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap animate-pulse">
                    🚀 COMING SOON
                  </span>
                </div>

                <div className="text-center mb-4 pt-2">
                  <h3 className="text-lg font-bold text-yellow-400 mb-1">
                    Elite Mentorship
                  </h3>
                  <p className="text-gray-300 text-xs">
                    1-on-1 expert guidance
                  </p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-extrabold text-white">$797</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">one-time payment</p>
                </div>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-white">Everything in Premium, plus:</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-300">2x 1-Hour Coaching Sessions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-300">Direct Mentor Access</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-300">Code Review on Capstones</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-300">Career Strategy Session</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-300">Portfolio Review</span>
                  </li>
                </ul>

                <button
                  disabled
                  className="block w-full text-center bg-gray-700 text-gray-400 text-sm font-bold px-4 py-3 rounded-lg cursor-not-allowed opacity-50"
                >
                  🏆 Launching Soon
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  Join waitlist for early access
                </p>
              </div>

            </div>

            {/* Value Props Below Pricing */}
            <div className="mt-16 grid md:grid-cols-3 gap-6 text-center">
              <div className="p-6">
                <div className="text-4xl mb-3">⚡</div>
                <h4 className="font-bold text-gray-900 mb-2">Start Immediately</h4>
                <p className="text-sm text-gray-600">
                  Instant access to all content after enrollment
                </p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">🎯</div>
                <h4 className="font-bold text-gray-900 mb-2">Learn at Your Pace</h4>
                <p className="text-sm text-gray-600">
                  No deadlines or expiration dates
                </p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">💼</div>
                <h4 className="font-bold text-gray-900 mb-2">Build Your Portfolio</h4>
                <p className="text-sm text-gray-600">
                  4 production-ready projects to showcase
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-4">
              Trusted by AI Professionals Worldwide
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join thousands of developers, PMs, and founders who&apos;ve mastered AI agent systems
            </p>
            {/* Social Proof Stats */}
            <div className="mt-6 flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-3xl">⭐</span>
                <div className="text-left">
                  <div className="font-bold text-cte-black">4.9/5.0</div>
                  <div className="text-gray-600">Average Rating</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-3xl">👥</span>
                <div className="text-left">
                  <div className="font-bold text-cte-black">2,500+</div>
                  <div className="text-gray-600">Students Enrolled</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-3xl">🎯</span>
                <div className="text-left">
                  <div className="font-bold text-cte-black">94%</div>
                  <div className="text-gray-600">Completion Rate</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-cte-blue hover:shadow-xl transition-all"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  &quot;{testimonial.content}&quot;
                </p>
                <div className="flex items-center">
                  <div className="text-4xl mr-4">{testimonial.image}</div>
                  <div>
                    <div className="font-bold text-cte-black">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* About/Instructor Section */}
        <section className="mb-20 bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 md:p-14 border border-gray-200">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-6">
                Built by AI Practitioners, for Practitioners
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This course is developed and maintained by <strong>CTE Consultants &amp; General Sales LLC</strong>,
                a team of experienced AI engineers and consultants who&apos;ve shipped production AI systems for
                Fortune 500 companies and fast-growing startups.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We&apos;ve condensed years of trial-and-error into a structured curriculum that teaches you
                exactly what works in production—no fluff, no outdated patterns, just battle-tested approaches
                to building reliable AI agents.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-cte-blue" />
                  <span className="text-sm font-semibold text-gray-700">Industry Experts</span>
                </div>
                <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-cte-blue" />
                  <span className="text-sm font-semibold text-gray-700">Production Experience</span>
                </div>
                <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-cte-blue" />
                  <span className="text-sm font-semibold text-gray-700">Continuous Updates</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
              <h3 className="text-xl font-bold text-cte-black mb-4">What Makes This Different?</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-cte-blue flex-shrink-0 mr-3 mt-0.5" />
                  <span className="text-gray-700"><strong>Real-world focus:</strong> Every module addresses actual production challenges</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-cte-blue flex-shrink-0 mr-3 mt-0.5" />
                  <span className="text-gray-700"><strong>Code-first approach:</strong> Complete implementations, not just concepts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-cte-blue flex-shrink-0 mr-3 mt-0.5" />
                  <span className="text-gray-700"><strong>Latest APIs:</strong> OpenAI Assistants Edition with cutting-edge features</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-cte-blue flex-shrink-0 mr-3 mt-0.5" />
                  <span className="text-gray-700"><strong>Portfolio projects:</strong> 4 capstones you can showcase to employers</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-cte-black mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about the course
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-cte-blue transition-colors"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-bold text-cte-black text-lg group-hover:text-cte-blue transition-colors">
                    {faq.question}
                  </span>
                  <svg
                    className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>

          {/* Money-Back Guarantee Badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-full px-8 py-4">
              <div className="text-3xl">💚</div>
              <div className="text-left">
                <div className="font-bold text-green-900">30-Day Money-Back Guarantee</div>
                <div className="text-sm text-green-700">Risk-free enrollment. Not satisfied? Get a full refund.</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
